import React, { useState } from "react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";

const INTERESTS = [
  { id: "magazine_ad",   fr: "Encart publicitaire dans le magazine",         en: "Magazine advertising space" },
  { id: "web_banner",    fr: "Bannière premium sur le site",                 en: "Premium web banner" },
  { id: "feature",       fr: "Mise en avant éditoriale (publi-rédactionnel)", en: "Sponsored editorial feature" },
  { id: "partnership",   fr: "Partenariat média / classement co-signé",       en: "Media partnership / co-signed ranking" },
  { id: "other",         fr: "Autre — projet sur mesure",                    en: "Other — custom" },
];

export default function Advertise() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    full_name: "", company: "", email: "", phone: "",
    interest: "magazine_ad", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/advertise", form);
      setStatus("ok");
      setForm({ full_name: "", company: "", email: "", phone: "", interest: "magazine_ad", message: "" });
    } catch (e2) {
      setStatus("err");
      setErr(e2?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10 pt-16 pb-24" data-testid="page-advertise">
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="overline mb-3">{lang === "fr" ? "Visibilité & business" : "Visibility & business"}</div>
          <h1 className="serif text-5xl md:text-6xl leading-[1.02]">
            {lang === "fr" ? "Apparaissez dans GAI." : "Be featured in GAI."}
          </h1>
          <p className="mt-5 text-lg text-[var(--gai-grey-700)]">
            {lang === "fr"
              ? "GLOBAL AFRIQUE INTERNATIONAL diffuse dans plus de 100 pays auprès d'une audience de dirigeants, d'investisseurs et d'institutions. Mettez votre marque, votre produit ou votre vision sous les yeux qui comptent."
              : "GLOBAL AFRIQUE INTERNATIONAL reaches over 100 countries among executives, investors and institutions. Put your brand, product or vision before the right eyes."}
          </p>

          <div className="mt-10 space-y-4">
            {[
              { k: "100+", v: lang === "fr" ? "Pays couverts" : "Countries" },
              { k: "1M+",  v: lang === "fr" ? "Lecteurs qualifiés" : "Qualified readers" },
              { k: "10+",  v: lang === "fr" ? "Années d'expertise éditoriale" : "Years of editorial expertise" },
            ].map((it, i) => (
              <div key={i} className="flex items-end gap-5 border-b border-[var(--gai-grey-200)] pb-3">
                <div className="serif text-4xl gai-gradient-gold leading-none w-24">{it.k}</div>
                <div className="text-sm text-[var(--gai-grey-700)]">{it.v}</div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 space-y-8" data-testid="advertise-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="gai-label">{t("quote.fullName")}</label>
              <input required value={form.full_name} onChange={onChange("full_name")} className="gai-input" data-testid="advertise-fullname" />
            </div>
            <div>
              <label className="gai-label">{t("quote.company")}</label>
              <input value={form.company} onChange={onChange("company")} className="gai-input" data-testid="advertise-company" />
            </div>
            <div>
              <label className="gai-label">{t("quote.email")}</label>
              <input required type="email" value={form.email} onChange={onChange("email")} className="gai-input" data-testid="advertise-email" />
            </div>
            <div>
              <label className="gai-label">{t("quote.phone")}</label>
              <input value={form.phone} onChange={onChange("phone")} className="gai-input" data-testid="advertise-phone" />
            </div>
          </div>

          <div>
            <label className="gai-label">{lang === "fr" ? "Type de visibilité souhaité" : "Visibility type"}</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {INTERESTS.map((it) => (
                <button
                  type="button"
                  key={it.id}
                  onClick={() => setForm((f) => ({ ...f, interest: it.id }))}
                  className={`px-4 py-2 text-[0.72rem] tracking-[0.18em] uppercase border ${
                    form.interest === it.id
                      ? "bg-[var(--gai-red)] text-[var(--gai-white)] border-[var(--gai-red)]"
                      : "border-[var(--gai-grey-200)] text-[var(--gai-black)] hover:border-[var(--gai-red)]"
                  }`}
                  data-testid={`advertise-interest-${it.id}`}
                >
                  {lang === "fr" ? it.fr : it.en}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="gai-label">{lang === "fr" ? "Votre projet" : "Your project"}</label>
            <textarea required rows={5} value={form.message} onChange={onChange("message")} className="gai-input resize-none" data-testid="advertise-message" />
          </div>

          <div className="flex items-center gap-4">
            <button disabled={status === "sending"} className="gai-btn-gold" data-testid="advertise-submit">
              {status === "sending" ? t("common.sending") : (lang === "fr" ? "Envoyer ma demande" : "Send")}
            </button>
            {status === "ok" && (
              <span className="text-[var(--gai-red)] text-sm" data-testid="advertise-success">
                {lang === "fr" ? "Reçu — l'équipe revient vers vous sous 48h." : "Received — we reply within 48h."}
              </span>
            )}
            {status === "err" && <span className="text-red-400 text-sm">{String(err)}</span>}
          </div>
        </form>
      </div>
    </div>
  );
}
